import React from "react";
import { Box, Button, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { Control, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ExternalRegisterDot } from "@/components/externalregister/ExternalRegisterDot";
import { getPatientExternalRegisterAlerts, type PatientWithExternalRegisterSummary } from "@/components/externalregister/patientSummary";
import type { ExternalRegisterPatientSummary, ExternalRegisterSummary } from "@/components/externalregister/types";
import { patientService } from "@/services";
import {
  getOtherRunningPatientStatusEncounters,
  type PatientStatusEncounterSummary,
} from "./patientStatusWidgetHelpers";
import { usePatientStatusWidgetRuntime } from "./patientStatusWidgetContext";

type PatientStatusWidgetProps = {
  control: Control<any>;
};

const getRunningEncounterLabel = (count: number, t: (key: string, options?: Record<string, unknown>) => string): string => (
  count === 1
    ? t("patient.status.runningEncounters.otherSingular")
    : t("patient.status.runningEncounters.otherPlural", { count })
);

const getCurrentEncounterId = (
  runtime: ReturnType<typeof usePatientStatusWidgetRuntime>,
  currentEncounterIdFromForm?: string,
) => runtime.currentEncounterId || currentEncounterIdFromForm;

const getRenderableAlerts = (
  runtime: ReturnType<typeof usePatientStatusWidgetRuntime>,
  externalRegisterSummary?: ExternalRegisterPatientSummary,
) => {
  if (!runtime.canRenderExternalRegisterClinical) return [];
  return getPatientExternalRegisterAlerts(externalRegisterSummary);
};

const AlertStatusTile: React.FC<{
  existingPatientId: string;
  alerts: ExternalRegisterSummary[];
  runtime: ReturnType<typeof usePatientStatusWidgetRuntime>;
}> = ({ existingPatientId, alerts, runtime }) => {
  const { t } = useTranslation();
  const hasAlerts = alerts.length > 0;
  const canOpenDetails = runtime.canOpenExternalRegisterSecureDetails && runtime.onOpenExternalRegisterDetails;

  return (
    <Box
      sx={{
        minWidth: 0,
        border: "1px solid",
        borderColor: hasAlerts ? "error.light" : "divider",
        borderRadius: 1,
        backgroundColor: hasAlerts ? "error.50" : "grey.50",
        px: 1,
        py: 0.875,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={0.75}>
            {hasAlerts ? <ExternalRegisterDot alerts={alerts} size={8} /> : null}
            <Typography variant="caption" color="text.secondary" noWrap>
              {t("patient.status.clinicalAlerts.title")}
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
            {hasAlerts ? t("patient.status.clinicalAlerts.alertPresent") : t("common.empty.none")}
          </Typography>
        </Box>
        {hasAlerts && canOpenDetails ? (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => runtime.onOpenExternalRegisterDetails?.(
              existingPatientId,
              alerts.length === 1 ? alerts[0].id : undefined,
            )}
            sx={{
              flexShrink: 0,
              minWidth: 0,
              px: 1,
              py: 0.25,
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
          >
            {t("patient.status.clinicalAlerts.viewDetails")}
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
};

const RunningEncounterStatusTile: React.FC<{
  count: number;
}> = ({ count }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        minWidth: 0,
        border: "1px solid",
        borderColor: count > 0 ? "warning.light" : "divider",
        borderRadius: 1,
        backgroundColor: count > 0 ? "warning.50" : "grey.50",
        px: 1,
        py: 0.875,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <Chip
          size="small"
          color={count > 0 ? "warning" : "default"}
          label={count}
          sx={{
            height: 22,
            minWidth: 30,
            fontWeight: 700,
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" noWrap>
            {t("patient.status.runningEncounters.title")}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
            {getRunningEncounterLabel(count, t)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

const PatientStatusWidgetView: React.FC<{
  existingPatientId: string;
  externalRegisterSummary?: ExternalRegisterPatientSummary;
  recentEncounters: PatientStatusEncounterSummary[];
  currentEncounterIdFromForm?: string;
  runtime: ReturnType<typeof usePatientStatusWidgetRuntime>;
  isFetching: boolean;
}> = ({
  existingPatientId,
  externalRegisterSummary,
  recentEncounters,
  currentEncounterIdFromForm,
  runtime,
  isFetching,
}) => {
  const { t } = useTranslation();
  const currentEncounterId = getCurrentEncounterId(runtime, currentEncounterIdFromForm);
  const runningEncounters = getOtherRunningPatientStatusEncounters(recentEncounters, currentEncounterId);
  const alerts = getRenderableAlerts(runtime, externalRegisterSummary);

  return (
    <Box
      data-testid="patient-status-widget"
      sx={(theme) => ({
        width: "100%",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        backgroundColor: theme.palette.background.paper,
        p: 1.25,
        overflow: "hidden",
      })}
    >
      <Stack gap={1}>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
            {t("patient.status.title")}
          </Typography>
          {isFetching ? <CircularProgress size={12} /> : null}
        </Stack>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "minmax(0, 1fr) minmax(0, 1fr)",
            },
            gap: 1,
          }}
        >
          <AlertStatusTile
            existingPatientId={existingPatientId}
            alerts={alerts}
            runtime={runtime}
          />
          <RunningEncounterStatusTile count={runningEncounters.length} />
        </Box>
      </Stack>
    </Box>
  );
};

const trimSearchName = (value?: string): string => String(value || "").trim();

const FetchedPatientStatusWidgetContent: React.FC<{
  existingPatientId: string;
  externalRegisterSummary?: ExternalRegisterPatientSummary;
  currentEncounterIdFromForm?: string;
  firstName?: string;
  lastName?: string;
  runtime: ReturnType<typeof usePatientStatusWidgetRuntime>;
}> = ({
  existingPatientId,
  externalRegisterSummary,
  currentEncounterIdFromForm,
  firstName,
  lastName,
  runtime,
}) => {
  const firstNameSearch = trimSearchName(firstName);
  const lastNameSearch = trimSearchName(lastName);
  const canSearchPatient = firstNameSearch.length > 0 && lastNameSearch.length > 0;
  const { data, isFetching } = patientService.useGetList(canSearchPatient ? {
    meta: {
      firstName: `*${firstNameSearch}*`,
      lastName: `*${lastNameSearch}*`,
      sort: {
        field: "lastModified",
        order: "desc",
      },
      $incRecentEncounters: true,
    },
  } as any : null);

  const selectedPatient = ((data?.data ?? []) as (PatientWithExternalRegisterSummary & {
    recentEncounters?: PatientStatusEncounterSummary[];
  })[]).find((patient) => patient.id === existingPatientId);
  const recentEncounters = Array.isArray(selectedPatient?.recentEncounters) ? selectedPatient.recentEncounters : [];

  return (
    <PatientStatusWidgetView
      existingPatientId={existingPatientId}
      externalRegisterSummary={externalRegisterSummary}
      recentEncounters={recentEncounters}
      currentEncounterIdFromForm={currentEncounterIdFromForm}
      runtime={runtime}
      isFetching={isFetching}
    />
  );
};

const PatientStatusWidgetContent: React.FC<PatientStatusWidgetProps & { existingPatientId: string }> = ({
  control,
  existingPatientId,
}) => {
  const runtime = usePatientStatusWidgetRuntime();
  const [
    firstName,
    lastName,
    externalRegisterSummary,
    localRecentEncounters,
    currentEncounterIdFromForm,
  ] = useWatch({
    control,
    name: [
      "firstName",
      "lastName",
      "_externalRegisterSummary",
      "_patientStatusRecentEncounters",
      "_encounterId",
    ],
  }) as [
    string | undefined,
    string | undefined,
    ExternalRegisterPatientSummary | undefined,
    PatientStatusEncounterSummary[] | undefined,
    string | undefined,
  ];

  if (!Array.isArray(localRecentEncounters)) {
    return (
      <FetchedPatientStatusWidgetContent
        existingPatientId={existingPatientId}
        externalRegisterSummary={externalRegisterSummary}
        currentEncounterIdFromForm={currentEncounterIdFromForm}
        firstName={firstName}
        lastName={lastName}
        runtime={runtime}
      />
    );
  }

  return (
    <PatientStatusWidgetView
      existingPatientId={existingPatientId}
      externalRegisterSummary={externalRegisterSummary}
      recentEncounters={localRecentEncounters}
      currentEncounterIdFromForm={currentEncounterIdFromForm}
      runtime={runtime}
      isFetching={false}
    />
  );
};

export const PatientStatusWidget: React.FC<PatientStatusWidgetProps> = ({ control }) => {
  const existingPatientId = useWatch({ control, name: "_existingPatientId" }) as string | undefined;
  if (!existingPatientId) return null;

  return <PatientStatusWidgetContent control={control} existingPatientId={existingPatientId} />;
};

export default PatientStatusWidget;
